/**
 * API functions for survey management
 */

import { SURVEY_ID_OFFSET } from '../constants/surveyConstants';
import { ASYNC_ANALYSIS_CONFIG, ANALYSIS_TASK_STATES } from '../config/constants';

/**
 * Convert survey ID to share ID (Base64 encoded with offset and randomization)
 */
export function surveyIdToShareId(surveyId: number): string {
  // Multiply survey ID by a large prime to spread out the values more
  const multipliedId = surveyId * 982451653; // Large prime number
  const offsetId = multipliedId + SURVEY_ID_OFFSET;
  const encoded = btoa(offsetId.toString()).replace(/=/g, '');
  return encoded;
}

/**
 * Convert share ID back to survey ID
 */
export function shareIdToSurveyId(shareId: string): number {
  // Add padding back if needed
  const padding = 4 - (shareId.length % 4);
  if (padding !== 4) {
    shareId += '='.repeat(padding);
  }

  const decoded = atob(shareId);
  const offsetId = parseInt(decoded);
  const multipliedId = offsetId - SURVEY_ID_OFFSET;
  // Reverse the multiplication to get original survey ID
  return multipliedId / 982451653; // Same prime number used in encoding
}

export interface ParsedQuestion {
  id: string;
  text: string;
  type: string;
  choices?: string[];
  allow_other?: boolean;
  scale_min?: number;
  scale_max?: number;
  ranking_count?: number;
  followup_instructions?: string;
  enable_ai_followup?: boolean;
  matrix_rows?: string[];
  matrix_columns?: string[];
  conjoint_attributes?: {name: string; levels: string[]}[];
  media_urls?: string[];
  description?: string;
}

/** Survey-level block metadata (question grouping / randomization). */
export interface SurveyBlockDefinition {
  id: string;
  label: string;
  randomize_within: boolean;
}

export interface Survey {
  id: number;
  name: string;
  title?: string;
  description?: string;
  instructions?: string;
  state: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  questions?: ParsedQuestion[];
  blocks?: SurveyBlockDefinition[] | null;
  sessions_count?: number;
  responses_count?: number;
  redirect_config?: {
    redirect_base_url: string;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  };
}

export interface SurveySession {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: any[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
  responses_count: number;
}

/**
 * Parse questions for survey creation (no database records created)
 */
export async function parseQuestionsForSurvey(
  questions: string[],
  goal: string = '',
  context: string = ''
): Promise<{ task_id: string }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      questions,
      goal,
      context,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create survey with approved questions
 */
export async function createSurveyWithQuestions(
  name: string,
  questions: ParsedQuestion[],
  title?: string,
  description?: string,
  instructions?: string,
  redirectConfig?: any,
  maxResponses?: number | null,
  blocks?: SurveyBlockDefinition[] | null
): Promise<{ survey_id: number; survey_data: Survey }> {
  const requestBody: any = {
    name,
    questions,
  };

  if (blocks !== undefined && blocks !== null) {
    requestBody.blocks = blocks;
  }
  if (title) requestBody.title = title;
  if (description) requestBody.description = description;
  if (instructions) requestBody.instructions = instructions;
  if (redirectConfig) requestBody.redirect_config = redirectConfig;
  if (maxResponses !== null && maxResponses !== undefined) {
    requestBody.max_responses = maxResponses;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get user's surveys
 */
export async function getUserSurveys(): Promise<{ surveys: Survey[] }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get specific survey details (authenticated)
 */
export async function getSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get survey details for public access (survey taking)
 */
export async function getPublicSurvey(surveyId: number): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/public`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get specific survey by ID with full details (alias for getSurvey)
 */
export const getSurveyById = getSurvey;

/**
 * Update survey state (draft, active, paused, completed)
 */
export async function updateSurveyState(
  surveyId: number,
  newState: string
): Promise<{ survey: Survey }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/state`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      state: newState,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Session start result types
 */
export type SessionStartStatus = 'success' | 'resumed' | 'quota_full' | 'already_completed' | 'screened_out';

export interface SessionStartResult {
  status?: SessionStartStatus;
  session_id?: number;
  survey_id?: number;
  session_data?: SurveySession;
  redirect_url?: string;
  message?: string;
  error?: string;
}

/**
 * Start or resume a survey session
 * If an external_tracking_id is provided and a session already exists,
 * the existing session is returned based on its state
 */
export async function startSurveySession(
  surveyId: number,
  externalTrackingId?: string
): Promise<SessionStartResult> {
  const requestBody: any = {};

  if (externalTrackingId) {
    requestBody.external_tracking_id = externalTrackingId;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Submit response to survey session
 */
export async function submitResponse(
  sessionId: number,
  questionId: string,
  responseData: any,
  questionType: string,
  questionStartTime?: string,
  advanceQuestion?: boolean,
  inputMode?: string
): Promise<{ session_data: SurveySession }> {
  const requestBody: any = {
    question_id: questionId,
    response_data: responseData,
    question_type: questionType,
  };

  if (questionStartTime) {
    requestBody.question_start_time = questionStartTime;
  }

  if (advanceQuestion !== undefined) {
    requestBody.advance_question = advanceQuestion;
  }

  if (inputMode) {
    requestBody.input_mode = inputMode;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Complete survey session
 */
export async function completeSurveySession(sessionId: number): Promise<{
  session_data: SurveySession;
  is_complete: boolean;
  redirect_url?: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get survey session details
 */
export async function getSurveySession(sessionId: number): Promise<{ session: SurveySession }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Send heartbeat to keep session alive and track activity
 */
export function sendSessionHeartbeat(sessionId: number): void {
  const url = `${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/heartbeat`;
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url);
  } else {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  }
}

/**
 * Submit follow-up response to existing question
 */
export async function submitFollowupResponse(
  sessionId: number,
  questionId: string,
  followupQuestion: string,
  followupResponse: string
): Promise<{ session_data: SurveySession }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/followup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
      followup_question: followupQuestion,
      followup_response: followupResponse,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Start async analysis of response for potential follow-up question
 */
export async function startAnalyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  context?: string
): Promise<{
  status: 'processing';
  task_id: string;
  message: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analyze-response`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question_id: questionId,
      question_text: questionText,
      user_response: userResponse,
      question_type: questionType,
      context: context,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Check status of analysis task
 */
export async function getAnalysisTaskStatus(
  sessionId: number,
  taskId: string
): Promise<{
  status: typeof ANALYSIS_TASK_STATES[keyof typeof ANALYSIS_TASK_STATES];
  analysis?: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  };
  error?: string;
  message: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/analysis-tasks/${taskId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll for analysis completion with timeout and progress callbacks
 */
export async function pollForAnalysisCompletion(
  sessionId: number,
  taskId: string,
  options: {
    timeout?: number; // milliseconds, default 60000 (60 seconds)
    pollInterval?: number; // milliseconds, default 1000 (1 second)
    onProgress?: (status: string, message: string) => void;
  } = {}
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  }
}> {
  const {
    timeout = ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
    pollInterval = ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
    onProgress
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    try {
      const result = await getAnalysisTaskStatus(sessionId, taskId);

      // Notify progress callback
      if (onProgress) {
        onProgress(result.status, result.message);
      }

      if (result.status === ANALYSIS_TASK_STATES.SUCCESS) {
        if (result.analysis) {
          return { analysis: result.analysis };
        } else {
          throw new Error('Analysis completed but no analysis data received');
        }
      } else if (result.status === ANALYSIS_TASK_STATES.FAILED) {
        throw new Error(result.error || 'Analysis task failed');
      }

      // Task is still pending or processing, wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));

    } catch (error) {
      // If it's a network error, retry after a short delay
      if (error instanceof Error && (error.message.includes('fetch') || error.message.includes('NetworkError'))) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }
      // Other errors should be thrown immediately
      throw error;
    }
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}

/**
 * Analyze response for potential follow-up question (maintains backward compatibility)
 * Now uses async processing with polling
 */
export async function analyzeResponseForFollowup(
  sessionId: number,
  questionId: string,
  questionText: string,
  userResponse: string,
  questionType: string,
  options?: {
    timeout?: number;
    pollInterval?: number;
    context?: string;
    onProgress?: (status: string, message: string) => void;
  }
): Promise<{
  analysis: {
    needs_followup: boolean;
    reason: string;
    followup_question?: string;
  }
}> {
  // Start the analysis
  const taskResponse = await startAnalyzeResponseForFollowup(
    sessionId,
    questionId,
    questionText,
    userResponse,
    questionType,
    options?.context
  );

  // Poll for completion
  return pollForAnalysisCompletion(sessionId, taskResponse.task_id, options);
}

/**
 * Get all responses for a survey - simple list view
 */
export async function getSurveyResponses(surveyId: number): Promise<{
  survey_id: number;
  total_responses: number;
  responses: Array<{
    session_id: number;
    completed_at: string;
    responses_count: number;
    external_tracking_id?: string | null;
    customer_id?: string | number | null;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

interface ResponseItem {
  type: 'single_choice' | 'multiple_choice' | 'yes_no' | 'rating_scale' | 'likert_scale' | 'short_text' | 'long_text' | 'dropdown' | 'nps' | 'numeric_input' | 'ranking' | 'matrix_rating' | 'max_diff' | 'conjoint_analysis' | 'original_response' | 'followup_question' | 'followup_response';
  value: any;
  timestamp: string;
}

/**
 * Get detailed responses for a specific session
 */
export async function getSessionResponses(sessionId: number): Promise<{
  session_id: number;
  completed_at: string;
  responses: Array<{
    question_text: string;
    question_type: string;
    response_data: ResponseItem[];
    created_at: string;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/sessions/${sessionId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated MaxDiff summary for a survey across all sessions
 */
export async function getSurveyMaxDiffSummary(surveyId: number): Promise<{
  survey_id: number;
  maxdiff_questions: Array<{
    question_id: string;
    question_text: string;
    total_sessions: number;
    total_sets: number;
    claims: Array<{
      claim: string;
      most_count: number;
      least_count: number;
      net_score: number;
      most_percentage: number;
      least_percentage: number;
      rank: number;
    }>;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/maxdiff-summary`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get aggregated Image Hotspot summary for a survey across all sessions
 */
export async function getSurveyImageHotspotSummary(surveyId: number): Promise<{
  survey_id: number;
  image_hotspot_questions: Array<{
    question_id: string;
    question_text: string;
    image_url: string;
    bounding_boxes: Array<{
      id: string;
      label: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
    ranking_count: number;
    total_sessions: number;
    total_clicks: number;
    aggregated_clicks: Array<{
      x: number;
      y: number;
      rank: number;
      product_id: string | null;
      product_label: string | null;
      timestamp_ms: number;
    }>;
    response_distribution: Array<{
      option: string;
      count: number;
      percentage: number;
      ranking_breakdown: Record<string, number>;
    }>;
  }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/image-hotspot-summary`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update survey questions, quota, and redirect config
 */
export async function updateSurveyQuestions(
  surveyId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  blocks?: SurveyBlockDefinition[] | null
): Promise<{
  survey: any;
}> {
  const body: any = { questions };

  if (blocks !== undefined) {
    body.blocks = blocks;
  }
  if (redirectConfig !== undefined) {
    body.redirect_config = redirectConfig;
  }

  if (maxResponses !== undefined) {
    body.max_responses = maxResponses;
  }

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/questions`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Survey Groups (between-subject distribution layer)
// ---------------------------------------------------------------------------

export interface SurveyGroup {
  id: number;
  name: string;
  group_count: number;
  group_labels: string[];
  survey_ids: number[];
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  allocation_ratios?: number[] | null;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface SurveyGroupCreatePayload {
  name: string;
  group_count: number;
  questions: any[];
  blocks?: SurveyBlockDefinition[] | null;
  title?: string;
  description?: string;
  instructions?: string;
  redirect_config?: any;
  max_responses_per_group?: number | null;
  allocation_ratios?: number[] | null;
}

export async function createSurveyGroup(
  payload: SurveyGroupCreatePayload
): Promise<{ group_id: number; group: SurveyGroup; distribution_url: string }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroups(): Promise<{ groups: SurveyGroup[] }> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function getSurveyGroupDetail(groupId: number): Promise<{
  group: SurveyGroup;
  session_counts: Record<number, { total: number; completed: number }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export function getSurveyGroupExportUrl(groupId: number): string {
  return `${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/export/xlsx`;
}

export async function getSurveyGroupResponses(groupId: number): Promise<{
  group_id: number;
  total_responses: number;
  responses: any[];
  per_group: Record<number, { label: string; responses: any[] }>;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/responses`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateSurveyGroupQuestions(
  groupId: number,
  questions: any[],
  redirectConfig?: any,
  maxResponses?: number | null,
  allocationRatios?: number[] | null,
  blocks?: SurveyBlockDefinition[] | null,
): Promise<{ group: SurveyGroup }> {
  const body: any = { questions };
  if (blocks !== undefined) body.blocks = blocks;
  if (redirectConfig !== undefined) body.redirect_config = redirectConfig;
  if (maxResponses !== undefined) body.max_responses = maxResponses;
  if (allocationRatios !== undefined) body.allocation_ratios = allocationRatios;

  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/survey-groups/${groupId}/questions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Update survey welcome screen data (title, description, instructions)
 */
export async function updateSurveyWelcomeData(
  surveyId: number,
  welcomeData: {
    title?: string;
    description?: string;
    instructions?: string;
  }
): Promise<{
  survey: Survey;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/welcome`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(welcomeData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// =============================================================================
// SURVEY ANALYSIS API
// =============================================================================

export interface SurveyAnalysisStatus {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  progress_message?: string;
  error_message?: string;
}

export interface DistributionItem {
  count: number;
  percentage: number;
  // Different question types use different field names for the label
  choice?: string;   // SingleChoice, Dropdown
  rating?: number;   // RatingScale
  level?: string;    // LikertScale
  score?: number;    // NPS
  value?: number;    // Numeric
}

export interface RankingItem {
  item?: string;        // Ranking
  option?: string;      // MaxDiff
  average_rank?: number;
  first_place_count?: number;
  last_place_count?: number;
  most_count?: number;  // MaxDiff
  least_count?: number; // MaxDiff
  net_score?: number;   // MaxDiff
}

export interface MatrixItem {
  item: string;
  average: number;
  count: number;
  distribution?: Record<number, number>;
}

export interface PreferenceItem {
  choice: string;
  count: number;
  percentage: number;
}

export interface ProductItem {
  product: string;
  total_clicks: number;
  first_choice_count: number;
  rank_distribution?: Record<number, number>;
}

export interface QuestionAnalysis {
  question_id: string;
  question_text: string;
  type: string;
  total_responses: number;
  tldr?: string;
  synthesis?: string;
  counter_arguments?: string[];
  distribution?: DistributionItem[];
  statistics?: Record<string, number>;
  responses?: (string | number | Record<string, unknown>)[];
  followup_responses?: string[];
  // Additional fields for specific question types
  rankings?: RankingItem[];      // Ranking, MaxDiff
  items?: MatrixItem[];          // Matrix
  preferences?: PreferenceItem[]; // Conjoint
  products?: ProductItem[];       // Image Hotspot
  // Yes/No fields
  yes_count?: number;
  no_count?: number;
  yes_percentage?: number;
  no_percentage?: number;
  // Percentage Allocation fields
  allocation_target?: number;
}

export interface SurveySynthesis {
  markdown: string;
}

export interface SurveyAnalysisResult {
  analysis_id: number;
  survey_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  question_analyses?: Record<string, QuestionAnalysis>;
  survey_synthesis?: SurveySynthesis;
  created_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface AnalysisHistoryItem {
  analysis_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  analysis_goal?: string;
  responses_analyzed: number;
  created_at?: string;
  completed_at?: string;
}

/**
 * Trigger a new AI analysis for a survey
 */
export async function triggerSurveyAnalysis(
  surveyId: number,
  analysisGoal?: string
): Promise<{
  analysis_id: number;
  task_id: string;
  status: string;
  message: string;
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      analysis_goal: analysisGoal,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get the latest analysis for a survey
 */
export async function getSurveyAnalysis(surveyId: number): Promise<SurveyAnalysisResult> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a specific analysis by ID
 */
export async function getSurveyAnalysisById(
  surveyId: number,
  analysisId: number
): Promise<SurveyAnalysisResult> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get analysis status and progress
 */
export async function getSurveyAnalysisStatus(
  surveyId: number,
  analysisId: number
): Promise<SurveyAnalysisStatus> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/${analysisId}/status`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get analysis history for a survey
 */
export async function getSurveyAnalysisHistory(surveyId: number): Promise<{
  analyses: AnalysisHistoryItem[];
}> {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/surveys/${surveyId}/analysis/history`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Poll for analysis completion
 */
export async function pollSurveyAnalysisCompletion(
  surveyId: number,
  analysisId: number,
  options: {
    timeout?: number;
    pollInterval?: number;
    onProgress?: (progress: number, message: string) => void;
  } = {}
): Promise<SurveyAnalysisResult> {
  const { timeout = 600000, pollInterval = 3000, onProgress } = options;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const status = await getSurveyAnalysisStatus(surveyId, analysisId);

    if (onProgress) {
      onProgress(status.progress, status.progress_message || '');
    }

    if (status.status === 'completed') {
      return getSurveyAnalysisById(surveyId, analysisId);
    }

    if (status.status === 'failed') {
      throw new Error(status.error_message || 'Analysis failed');
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  throw new Error(`Analysis timeout after ${timeout / 1000} seconds`);
}
